<template>
  <div class="timeline-item-content">
    <!-- <pre>{{ activity_content }}</pre> -->
    <div class="timeline-item-custom-content">
      <slot></slot>
    </div>
    <div class="flex text-sm text-gray-600 mb-2" v-if="!isNullOrEmpty(reference_no)">
      <span class="font-semibold">Customer Reference No:</span>
      <span class="px-2 py-1 text-xs">{{ reference_no }}</span>
    </div>
    <div
      v-if="activity_content.content_group && activity_content.content_group.length > 0"
      class="timeline-item-images grid grid-cols-3"
    >
      <div
        class="h-max"
        v-for="(content, index) in activity_content.content_group"
        :key="`image-${index}`"
      >
        <div
          v-if="
            content.third_party_approval_obj && content.third_party_approval_obj.approval_status
          "
          class="mb-2"
        >
          <span
            class="badge"
            :class="{
              'badge-warning': content.third_party_approval_obj.approval_status === 'pending',
              'badge-success': content.third_party_approval_obj.approval_status === 'accepted',
              'badge-danger': content.third_party_approval_obj.approval_status === 'rejected',
            }"
          >
            Third Party Status:
            {{
              content.third_party_approval_obj.approval_status.charAt(0).toUpperCase() +
              content.third_party_approval_obj.approval_status.slice(1)
            }}
          </span>
        </div>

        <div
          class="border border-[#F5F5F5] rounded-lg p-3 h-full"
          v-if="content.images && content.images.length > 0"
        >
          <div
            class="flex items-center gap-2 mb-2 justify-between"
          >
            <div class="flex min-h-[22px] items-center">
              <div class="text-sm text-gray-600 mb-2" v-if="content.nickName">
                <span class="font-semibold">Nick Name:</span>
                <span class="px-2 py-1 text-xs">
                  {{ content.nickName }}
                </span>
              </div>
              <div v-else>
                <div class="h-full"></div>
              </div>
            </div>
            
            <!-- <button class="bg-primary text-white rounded-md text-center p-1">
              <QueueListIcon class="size-6" />
            </button> -->
          </div>
          <div class="grid grid-cols-2">
            <template v-for="(image, index) in content.images" :key="index">
              <template v-if="!image.url || image.url !== 'null'">
                <template v-if="['jpg', 'jpeg', 'png', 'bmp', 'svg'].includes(image.ext)">
                  <img
                    :src="image && image.url ? `${storage_url}/${image?.url}` : imgPlaceholder"
                    :alt="image?.alt || ''"
                    class="object-contain h-[200px] max-h-[200px]"
                    @click="onImageClick(image?.url ?? null)"
                  />
                </template>
                <DocumentTextIcon v-else class="w-full text-gray-200" />
              </template>
            </template>
          </div>
          <div
            v-if="
              content.third_party_approval_obj &&
              content.third_party_approval_obj.approval_status === 'rejected' &&
              content.third_party_approval_obj.feedback
            "
            class="feedback-ui-enhanced mt-2"
          >
            <div class="feedback-title">
              <span class="text-danger mr-2 font-weight-bold">Rejection Feedback</span>
            </div>
            <div class="feedback-body">
              {{ content.third_party_approval_obj.feedback }}
            </div>
          </div>
        </div>
        <div
          v-if="!isNullOrEmpty(content.design_id) && content.images.length > 0"
          class="text-sm text-gray-600 mb-2"
        >
          <span class="font-semibold">Design ID:</span>
          <span class="px-2 py-1 text-xs">{{ content.design_id }}</span>
        </div>

        <div v-if="content.addons.length > 0" class="flex gap-2 text-sm text-gray-600 mb-2">
          <div class="flex flex-wrap gap-1">
          <span class="font-semibold pr-1">Addons:</span>
            <span
              v-for="addon in content.addons"
              :key="`addon-${addon.addon_id}`"
              class="px-2 py-1 bg-[#DEEDFF] rounded-lg text-[12px] font-normal"
            >
              {{ addon.title }}
            </span>
          </div>
        </div>

        <div v-if="!isNullOrEmpty(content.skipReason)" class="flex gap-2 mb-2 text-sm">
          <span class="font-semibold">Skip Reason:</span>
          <span class="px-2 py-1 text-xs">
            {{ content.skipReason }}
          </span>
        </div>

        <div v-if="!isNullOrEmpty(content.message)" class="flex gap-2 mb-2 text-sm">
          <span class="font-semibold">Feedback:</span>
          <span class="text-gray-600">{{ content.message }}</span>
        </div>
        <div
          v-if="activity_content.activity_status === FACTORYREVIEW && !isNullOrEmpty(content.reorder_message)"
          class="text-sm text-gray-600"
        >
          {{ content.reorder_message }}
        </div>
      </div>
    </div>
    <template v-if="activity_content.general_comments">
      <strong class="font-bold">General Comments: </strong>
      <span class="text-sm text-gray-600">
        {{ activity_content.general_comments }}
      </span>
    </template>
    <div
      v-if="
        activity_content.activity_status === 'quality_control' &&
        activity_content.content_group.find((item) => 'quality_control' in item)
      "
      class="text-sm text-gray-600 w-full"
    >
      <ol class="list-decimal list-inside">
        <li
          v-for="(report, index) in activity_content.content_group.find(
            (item) => 'quality_control' in item
          )?.quality_control.reports"
          :key="`report-${index}`"
        >
          QC report {{ report.status }}:
          <a class="text-blue-600" :href="report.pdf_url" target="_blank">
            {{ decodeURIComponent(report.pdf_name) }}
          </a>
          submitted at {{ formatDate(report.created_at, 'dd/MM/yyyy') }}
        </li>
      </ol>
    </div>
    <div class="timeline-item-custom-content">
      <slot name="comments"> </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import imgPlaceholder from '@/assets/img/placeholder.png';
import { CustimooOrderFlowStatuses, isNullOrEmpty } from '@/composables';
import { Addon, QcReport } from '@/models';
import { DocumentTextIcon } from '@heroicons/vue/24/solid';
import { formatDate } from 'date-fns';
const { FACTORYREVIEW } = CustimooOrderFlowStatuses;
interface Image {
  url: string;
  alt?: string;
  ext?: string;
}
interface Content {
  images: Image[];
  nickName: string;
  addons: Addon[];
  skipReason: string;
  status: string;
  message: string;
  reorder_message: string;
  third_party_approval_obj: any;
  design_id?: string | null;
  quality_control?: QcReport;
}
interface ActivityContent {
  content_group?: Content[];
  activity_status?: string;
  general_comments?: string | null;
}
interface Props {
  activity_content?: ActivityContent;
  reference_no?: string;
  order_item_id?: number | string;
}

withDefaults(defineProps<Props>(), {
  activity_content: () => ({}),
});

const storage_url = import.meta.env.VITE_APP_FILE_URL;
const emit = defineEmits(['showPreview']);

const onImageClick = (image_url) => {
  if (image_url) {
    emit('showPreview', image_url);
  }
  return;
};
</script>

<style scoped lang="scss">
.badge {
  display: inline-block;
  padding: 0.25em 0.7em;
  font-size: 0.85em;
  border-radius: 0.5em;
  font-weight: 500;
}
.badge-warning {
  background: #f9b115;
  color: #4f5d73;
}
.badge-success {
  background: #2eb85c;
  color: white;
}
.badge-danger {
  background: #e55353;
  color: white;
}
.timeline-item-content {
  .timeline-item-description {
    margin-bottom: 1rem;
    font-size: 0.875rem;
    line-height: 1.5;
    color: #4b5563; /* gray-600 */
  }

  .timeline-item-images {
    gap: 0.5rem;
    margin-bottom: 1rem;

    .timeline-item-image {
      img {
        width: 100%;
        height: 100%;
      }
    }
  }

  .timeline-item-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;

    .timeline-item-action-button {
      padding: 0.5rem 1rem;
      background-color: #f3f4f6; /* gray-100 */
      border: 1px solid #e5e7eb; /* gray-200 */
      border-radius: 0.25rem;
      font-size: 0.875rem;
      color: #374151; /* gray-700 */
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background-color: #e5e7eb; /* gray-200 */
      }
    }
  }
}
.timeline-item-custom-content {
  height: max-content;
}
.feedback-ui-enhanced {
  background: #fff0f0;
  border: 1px solid #ffa39e;
  border-radius: 6px;
  padding: 0.5em 1em;
  margin-bottom: 0.5em;
}
.feedback-title {
  font-size: 0.95em;
  margin-bottom: 0.2em;
}
.feedback-body {
  color: #cf1322;
  font-size: 0.95em;
}
.text-danger {
  color: #cf1322;
}
.font-weight-bold {
  font-weight: bold;
}
</style>
